import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';
import isEmpty from 'lodash.isempty';
import { conditionNames } from '@appcues/libcues';

import { CButton, CPage, Column } from '@appcues/component-library';
import ChildPage from 'components/Common/ChildPage';

import { ContentStatus, H1 } from 'next/components/ui';
import EventTriggering from 'components/StepSettings/EventTriggering';
import AudienceTargeting from 'components/Common/AudienceTargeting';
import ABTesting from 'components/StepSettings/ABTesting';
import GoalSetting from 'components/StepSettings/GoalSetting';
import RedirectActionSetting from 'components/StepSettings/RedirectActionSetting';
import ContentTriggering from 'components/StepSettings/ContentTriggering';
import FlowInformationPanel from 'components/StepSettings/FlowInformationPanel';
import Taco from 'components/StepSettings/Taco';
import Publishing from 'components/Common/Publishing';
import FlowStatusBanner from 'components/Common/FlowStatusBanner';
import Panel from 'components/Common/Panel';
import Loader from 'components/Common/Loader';
import PageTargeting from 'components/Common/PageTargeting';
import Toaster from 'components/Common/Toaster';
import Localization from 'components/StepSettings/Localization';
import { STEP_TYPE_IDS, STEP_TYPES } from 'constants/stepTypes';
import {
  CREATE_SEGMENT_MODAL,
  DEFINE_GOAL_MODAL,
  PERMALINK_MODAL,
} from 'constants/globals';
import {
  callApi as fetchRule,
  update as updateRule,
} from 'actions/account/rules';
import { navigate } from 'actions/routing';
import { update as updateFlow } from 'actions/account/flows';
import { startPolling, stopPolling } from 'actions/account/flowStatus';
import { selectFlowStatus } from 'reducers/account/flowStatus';

import { selectAccountRule } from 'reducers/account/rules';
import { selectFlow, selectAreFlowsSynced } from 'reducers/account/flows';
import {
  selectAccountConditionsSynced,
  selectAccountStepConditions,
} from 'reducers/account/conditions';
import {
  selectAccountSegments,
  selectAccountSegmentsSynced,
} from 'reducers/account/segments';
import {
  selectAccountMeta,
  selectAccountMetaSynced,
} from 'reducers/account/meta';
import {
  selectAccountGoals,
  selectAccountGoalsSynced,
} from 'reducers/account/goals';
import { selectUserRole } from 'reducers/account/users';
import { selectUserId } from 'reducers/user';
import { includesCrxParam } from 'utils/url';

import {
  selectAccountFeature,
  selectAccountFeaturesSynced,
} from 'reducers/account/features';
import {
  EVENT_ATTRIBUTE_TRIGGERING,
  EVENT_TRIGGERING,
  WIDGET_TARGETING,
  AB_TESTING,
} from 'constants/features';
import { selectIsInstalled } from 'reducers/account/installedDomains';
import { showModal } from 'actions/currentModal';
import {
  addClauseAndFlushRule,
  addGroupClauseAndFlushRule,
  deleteClauseAndFlushRule,
  deleteMatchingClausesAndFlushRule,
  dissolveGroupClauseAndFlushRule,
  replaceClauseAndFlushRule,
  setAudienceAllUsersAndFlushRule,
  setAudienceCustomSegmentAndFlushRule,
  setAudienceSegmentAndFlushRule,
  setDisplayAnyPageAndFlushRule,
  setDisplaySpecificPageAndFlushRule,
  updateClauseAndFlushRule,
} from 'actions/account/conditions';
import Prioritization from 'components/StepSettings/Prioritization';

const { hasOwnProperty } = Object.prototype;

export class StepSettings extends Component {
  componentDidMount() {
    const { actions, stepId } = this.props;
    this.componentDidUpdate({});
    actions.startPolling(stepId);
    actions.onLoad();
  }

  componentDidUpdate(prevProps) {
    const { loaded, step, actions } = this.props;

    if (loaded && !prevProps.loaded && !step) {
      Toaster.show({
        message: "You don't have access to this flow—redirecting to Overview",
      });
      actions.stopPolling();
      window.setTimeout(() => actions.navigate('/'), 1000);
    }
  }

  componentWillUnmount() {
    const { actions } = this.props;
    actions.stopPolling();
  }

  handlePermalinkClick = () => {
    const { actions, rule, meta, isInstalled } = this.props;
    actions.showModal(PERMALINK_MODAL, {
      stepId: rule.id,
      isInstalled,
      accountId: meta.id,
    });
  };

  render() {
    const {
      goals,
      loaded,
      rule,
      step,
      conditions,
      meta,
      segments,
      actions,
      contentStatus,
      isInCRX,
      isPublished,
      hasWidgetTargeting,
      hasEventTriggering,
      hasEventAttributeTriggering,
      hasABTesting,
      role,
    } = this.props;

    if (!loaded || !step.id) {
      return <Loader />;
    }

    const stepProperties = STEP_TYPES[step.type];
    const lastUpdatedAtTimestamp = step.updatedAt;

    const isSatisfactionStepType =
      stepProperties.id === STEP_TYPE_IDS.SATISFACTION;
    const isChecklistStepType = stepProperties.id === STEP_TYPE_IDS.CHECKLIST;
    const { contentType } = stepProperties;

    const showEventTriggering =
      hasEventTriggering ||
      conditions.find(
        clause => clause.conditionName === conditionNames.TRIGGER
      );

    return (
      <ChildPage
        components={{
          Title: () => (
            <>
              <H1>{step.name}</H1>
              {!isEmpty(contentStatus) && (
                <ContentStatus published={isPublished} state={step.state} />
              )}
            </>
          ),
        }}
        breadcrumbContents={{
          label: `Flow details`,
          path: `/${contentType}/${step.id}/analytics`,
        }}
        subHeaderActions={
          <>
            {isPublished && !isSatisfactionStepType && !isChecklistStepType && (
              <CButton onClick={this.handlePermalinkClick}>Permalink</CButton>
            )}
            <Publishing
              contentStatus={contentStatus}
              enableTestMode
              flowName={step.name}
              conditions={conditions}
              isPublished={isPublished}
              previewUrl={step.previewUrl}
              stepId={step.id}
              stepType={step.type}
              showTestOptions
              isInCRX={isInCRX}
              lastUpdatedAtTimestamp={lastUpdatedAtTimestamp}
              existingTestFlowId={rule.testFlowId}
              role={role}
            />
          </>
        }
      >
        <CPage.Container>
          <FlowStatusBanner
            isPublished={isPublished}
            isPublishedAndDirty={contentStatus.publishedAndDirty}
          />
          <Column>
            <FlowInformationPanel
              shouldShowOpenInBuilderAction={
                !isInCRX && !isSatisfactionStepType && !isChecklistStepType
              }
              isLocalized={step.lockedForLocalization}
              id={step.id}
              name={step.name}
              previewUrl={step.previewUrl}
              onSubmit={actions.onSave}
            />
            <Panel
              name="Trigger"
              description="Define when this flow launches."
              showLine={false}
            >
              <EventTriggering
                stepId={rule.id}
                clauses={conditions}
                rule={rule}
                addClause={actions.addClauseAndFlushRule}
                updateClause={actions.updateClauseAndFlushRule}
                deleteClause={actions.deleteClauseAndFlushRule}
                deleteMatchingClauses={
                  actions.deleteMatchingClausesAndFlushRule
                }
                setStepFrequency={actions.setStepFrequency}
                hasEventTriggering={showEventTriggering}
                hasEventTriggeringWithAttributes={hasEventAttributeTriggering}
              />
            </Panel>
            <Panel
              name="Page targeting"
              description="Define where the flow should appear."
            >
              <PageTargeting
                addClause={actions.addClauseAndFlushRule}
                clauses={conditions}
                deleteClause={actions.deleteClauseAndFlushRule}
                deleteMatchingClauses={
                  actions.deleteMatchingClausesAndFlushRule
                }
                showManualTriggerOption
                meta={meta}
                previewUrl={step.previewUrl}
                rule={rule}
                setDisplayAnyPage={actions.setDisplayAnyPageAndFlushRule}
                setDisplaySpecificPage={
                  actions.setDisplaySpecificPageAndFlushRule
                }
                setStepFrequency={actions.setStepFrequency}
                stepId={rule.id}
                updateClause={actions.updateClauseAndFlushRule}
              />
            </Panel>

            <Panel
              name="Audience"
              description="Define who should see the flow."
            >
              <AudienceTargeting
                isInCRX={isInCRX}
                stepId={rule.id}
                clauses={conditions}
                segments={segments}
                role={role}
                addGroupClause={actions.addGroupClauseAndFlushRule}
                dissolveGroupClause={actions.dissolveGroupClauseAndFlushRule}
                addClause={actions.addClauseAndFlushRule}
                updateClause={actions.updateClauseAndFlushRule}
                replaceClause={actions.replaceClauseAndFlushRule}
                deleteClause={actions.deleteClauseAndFlushRule}
                setAudienceAllUsers={actions.setAudienceAllUsersAndFlushRule}
                setAudienceCustomSegment={
                  actions.setAudienceCustomSegmentAndFlushRule
                }
                setAudienceSegment={actions.setAudienceSegmentAndFlushRule}
                createSegmentFromRule={onCreateCallback => () =>
                  actions.showModal(CREATE_SEGMENT_MODAL, {
                    stepId: rule.id,
                    createSegmentFromRule: actions.createSegmentFromRule,
                    segments,
                    onCreateCallback,
                  })}
              />
              {hasABTesting && (
                <ABTesting
                  stepId={rule.id}
                  clauses={conditions}
                  addClause={actions.addClauseAndFlushRule}
                  updateClause={actions.updateClauseAndFlushRule}
                  deleteClause={actions.deleteClauseAndFlushRule}
                />
              )}

              {hasWidgetTargeting && (
                <div>
                  <h4>Allow users to recall this flow?</h4>
                  <Taco
                    rule={rule}
                    setWidgetFilter={actions.setStepWidgetFilter}
                  />
                </div>
              )}
            </Panel>

            <Prioritization flowId={rule.id} />

            <Localization flowId={rule.id} />

            <Panel
              name="Goal"
              description="Choose how to measure the success of this flow based on what users accomplished after seeing it."
            >
              <GoalSetting
                setGoal={actions.onChangeGoal}
                removeGoalFromRule={actions.onRemoveGoal}
                stepId={rule.id}
                goals={rule.goals}
                accountGoals={goals}
                showDefineGoalModal={props => {
                  actions.showModal(DEFINE_GOAL_MODAL, {
                    ...props,
                    stepId: rule.id,
                  });
                }}
              />
            </Panel>

            <Panel
              name="Actions"
              description="Set action for after this flow has completed."
              showLine={false}
            >
              <h4>What should we do when this flow has completed?</h4>
              <RedirectActionSetting
                redirectUrl={rule.redirectUrl}
                onBlur={actions.setStepRedirectUrl}
                onClear={actions.clearStepRedirectUrl}
                labelText="Redirect to a different page"
                placeholderText="Redirect URL"
              />
              <ContentTriggering
                rule={rule}
                setNextStepId={actions.setNextStepId}
              />
              <div className="content">
                <span className="help-text">
                  Note: these actions won&apos;t be triggered during preview,
                  but they will work in the live flow.
                </span>
              </div>
            </Panel>
          </Column>
        </CPage.Container>
      </ChildPage>
    );
  }
}

function mapStateToProps(state, routeProps) {
  const { stepId } = routeProps.match.params;
  const isInCRX = includesCrxParam(routeProps);
  const contentStatus = selectFlowStatus(state, stepId);
  const conditions = selectAccountStepConditions(state, stepId);

  return {
    hasEventTriggering: selectAccountFeature(state, EVENT_TRIGGERING),
    hasEventAttributeTriggering: selectAccountFeature(
      state,
      EVENT_ATTRIBUTE_TRIGGERING
    ),
    hasWidgetTargeting: selectAccountFeature(state, WIDGET_TARGETING),
    hasABTesting: selectAccountFeature(state, AB_TESTING),
    meta: selectAccountMeta(state),
    isInstalled: selectIsInstalled(state),
    rule: selectAccountRule(state, stepId),
    step: selectFlow(state, stepId),
    stepId,
    conditions,
    segments: selectAccountSegments(state),
    contentStatus,
    goals: selectAccountGoals(state),
    role: selectUserRole(state, selectUserId(state)),
    isInCRX,
    isPublished: hasOwnProperty.call(contentStatus, 'publishedAndDirty'),
    loaded:
      conditions.length > 0 &&
      selectAccountMetaSynced(state) &&
      selectAccountGoalsSynced(state) &&
      selectAreFlowsSynced(state) &&
      selectAccountConditionsSynced(state) &&
      selectAccountSegmentsSynced(state) &&
      selectAccountFeaturesSynced(state),
  };
}

function mapDispatchToProps(dispatch, ownProps) {
  const { stepId } = ownProps.match.params;
  return {
    actions: bindActionCreators(
      {
        onLoad: () => fetchRule({ id: stepId }),
        showModal,
        addClauseAndFlushRule,
        updateClauseAndFlushRule,
        deleteClauseAndFlushRule,
        deleteMatchingClausesAndFlushRule,
        setDisplayAnyPageAndFlushRule,
        setDisplaySpecificPageAndFlushRule,
        addGroupClauseAndFlushRule,
        dissolveGroupClauseAndFlushRule,
        replaceClauseAndFlushRule,
        setAudienceAllUsersAndFlushRule,
        setAudienceCustomSegmentAndFlushRule,
        setAudienceSegmentAndFlushRule,
        onSave: updateFlow,
        setStepRedirectUrl: url => updateRule(stepId, { redirectUrl: url }),
        clearStepRedirectUrl: () =>
          updateRule(stepId, { redirectUrl: null, redirectNewTab: null }),
        setStepFrequency: (id, frequency) => updateRule(id, { frequency }),
        setNextStepId: (id, nextContentId) => updateRule(id, { nextContentId }),
        setStepWidgetFilter: (id, widgetFilter) =>
          updateRule(id, { widgetFilter }),
        onRemoveGoal: id => updateRule(id, { goals: [] }),
        onChangeGoal: (id, goalId) => updateRule(id, { goals: [goalId] }),
        startPolling,
        stopPolling,
        navigate,
      },
      dispatch
    ),
  };
}

// language=SCSS prefix=dummy{ suffix=}
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(StepSettings)`
  width: 100%;

  &.fixed-header {
    .page-header {
      position: sticky;
      top: 0;
      left: 0;
      right: 0;
      z-index: 1001;

      padding-top: 12px;

      background-color: white;
    }

    main.page-container {
      padding-bottom: 40px;
    }
  }

  hr {
    height: 0;
    border: none;
    border-bottom: 2px solid ${props => props.theme['$gray-1']};
    margin: 1.25rem 0 2rem;
  }

  .option {
    .content {
      width: 100%;
    }
  }

  .legend {
    display: flex;
    flex-direction: row;
    padding: 12px 23px 0;
    label {
      font-size: 12px;
      margin-right: 25px;
      color: ${props => props.theme['$gray-30']};
      i {
        margin-right: 8px;
        &.live {
          color: ${props => props.theme.$green};
        }
        &.draft {
          color: ${props => props.theme['$gray-30']};
        }
      }
    }

    .content {
      padding: 5px 23px 0;
      .help-text {
        color: ${props => props.theme['$gray-30']};
        font-style: italic;
        margin: 5px 0;
        a {
          color: ${props => props.theme['$gray-30']};
          text-decoration: underline;
          cursor: pointer;
          &:hover {
            color: ${props => props.theme.$blue};
            text-decoration: underline;
          }
        }
      }
    }
  }

  .preview-url .preview-url-text {
    display: inline-block;
    font-size: 16px;
    margin-left: 0.5rem;
    min-width: auto;
    overflow: hidden;
    padding-right: 0;

    :before {
      display: inline;
    }

    :after {
      justify-self: flex-end;
      align-self: center;
      position: static;
    }
  }

  .settings-header {
    display: flex;
    flex-direction: row;

    .title-container {
      display: flex;
      flex-direction: row;
      margin-bottom: 0.5em;
      margin-top: 0.5em;
      align-items: center;

      .flow-status-tag {
        align-self: flex-start;
        margin: 16px 50px 0 10px;
      }

      fieldset {
        margin: 0;
        padding: 0;
        border: none;
        &:disabled {
          input {
            color: ${props => props.theme['$gray-50']};
            &::placeholder {
              color: ${props => props.theme['$gray-30']};
              font-style: italic;
            }
          }
        }
      }
    }
  }
`);
